import { useEffect, useState } from 'react';
import { adminApi, getErrorMessage, type DashboardData } from '../lib/api';

export default function Dashboard() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await adminApi.getDashboard();
        if (!cancelled) setData(d);
      } catch (err) {
        if (!cancelled) setError(getErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div className="text-gray-400">Loading dashboard...</div>;
  if (error) return <div className="text-red-400">Error: {error}</div>;
  if (!data) return <div className="text-gray-400">No data</div>;

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold">Dashboard</h2>

      {/* Metric cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Active Events" value={data.active_event_count} />
        <MetricCard label="Active Premium" value={data.premium.active_premium} />
        <MetricCard
          label="Conversion Rate"
          value={`${data.premium.conversion_rate.toFixed(2)}%`}
        />
        <MetricCard
          label="Cosmetics Sold"
          value={data.cosmetic_sales.total_sold}
        />
      </div>

      {/* Cache health (purgeless invalidation surface) */}
      <Section title="Cache Health">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Stat label="Purge Successes" value={data.cache_health.purge_success_count} />
          <Stat
            label="Purge Failures"
            value={
              data.cache_health.purge_failure_count > 0
                ? `⚠ ${data.cache_health.purge_failure_count}`
                : 0
            }
          />
          <Stat label="Last Purge" value={formatTs(data.cache_health.purge_last_success_ms)} />
          <Stat label="Versioned Cells" value={data.cache_health.versioned_cells} />
          <Stat label="Cells Bumped (24h)" value={data.cache_health.cells_bumped_24h} />
          <Stat
            label="Last Failure"
            value={
              data.cache_health.purge_failure_count > 0
                ? formatTs(data.cache_health.purge_last_failure_ms)
                : '—'
            }
          />
        </div>
        {data.cache_health.purge_failure_count > 0 && (
          <p className="text-xs text-yellow-300 mt-3">
            A purge reported failure, so the edge may still be serving pre-seed content. Re-run the
            reset from the Operations page.
          </p>
        )}
      </Section>

      {/* LiveOps summary */}
      <Section title="Live Ops Summary">
        <div className="grid grid-cols-3 gap-4">
          <Stat label="Total Participants" value={data.liveops.total_participants} />
          <Stat label="Completed" value={data.liveops.completed} />
          <Stat
            label="Completion Rate"
            value={`${data.liveops.completion_rate.toFixed(1)}%`}
          />
        </div>
      </Section>

      {/* Top cosmetics */}
      <Section title="Top Cosmetics by Sales">
        {data.cosmetic_sales.top_cosmetics.length === 0 ? (
          <p className="text-gray-500 text-sm">No cosmetics sold yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-400 border-b border-gray-800">
                <th className="py-2">Emoji</th>
                <th>ID</th>
                <th>Type</th>
                <th className="text-right">Sold</th>
                <th className="text-right">Limit</th>
              </tr>
            </thead>
            <tbody>
              {data.cosmetic_sales.top_cosmetics.map((c) => (
                <tr key={c.id} className="border-b border-gray-800/50">
                  <td className="py-2 text-lg">{c.emoji}</td>
                  <td className="font-mono text-xs">{c.id}</td>
                  <td>{c.type}</td>
                  <td className="text-right">{c.stock_sold}</td>
                  <td className="text-right text-gray-400">{c.stock_limit ?? '∞'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      {/* DAU graph */}
      <Section title="DAU (Last 14 Days)">
        {data.dau_graph.length === 0 ? (
          <p className="text-gray-500 text-sm">No DAU data available. Run the KPI snapshot cron to populate.</p>
        ) : (
          <div className="space-y-1">
            {data.dau_graph.map((d) => (
              <div key={d.date_text} className="flex items-center gap-3 text-sm">
                <span className="text-gray-400 w-24 font-mono">{d.date_text}</span>
                <div className="flex-1 bg-gray-800 rounded h-5 relative overflow-hidden">
                  <div
                    className="bg-brand-600 h-full"
                    style={{ width: `${Math.min(100, (d.dau / Math.max(...data.dau_graph.map((g) => g.dau), 1)) * 100)}%` }}
                  />
                </div>
                <span className="w-12 text-right">{d.dau}</span>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <p className="text-sm text-gray-400">{label}</p>
      <p className="text-2xl font-bold mt-1">{value}</p>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
      <h3 className="text-lg font-semibold mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div>
      <p className="text-sm text-gray-400">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

/** Epoch-ms → a short local timestamp, or an em dash when the counter has never been written. */
function formatTs(ms: number): string {
  if (!ms || ms <= 0) return '—';
  return new Date(ms).toLocaleString();
}
